import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { TouEditor, hoursToText, textToHours } from "@/components/TouEditor";
import { X } from "lucide-react";

const EMPTY = { elec_rate: "", gas_rate: "", water_rate: "", currency: "" };

/** Per-building energy rates so costs match the real bill, plus who gets the monthly report. */
export function SiteSettingsDialog({ siteId, siteDis, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [recipients, setRecipients] = useState([]);
  const [entry, setEntry] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [touEnabled, setTouEnabled] = useState(false);
  const [touPeriods, setTouPeriods] = useState([]);
  const [template, setTemplate] = useState([]);
  const [demandCharge, setDemandCharge] = useState("");
  const [demandBasis, setDemandBasis] = useState("monthly_peak");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get(`/sites/${encodeURIComponent(siteId)}/settings`).then(({ data }) => {
      setForm({
        elec_rate: data.elec_rate ?? "",
        gas_rate: data.gas_rate ?? "",
        water_rate: data.water_rate ?? "",
        currency: data.currency ?? "",
      });
      setRecipients(data.report_recipients || []);
      setEnabled(!!data.monthly_report_enabled);
      setTouEnabled(!!data.tou_enabled);
      setDemandCharge(data.demand_charge ?? "");
      setDemandBasis(data.demand_charge_basis || "monthly_peak");
      setTemplate(data.tou_template || []);
      setTouPeriods((data.tou_periods || []).map((p) => ({
        ...p, rate: String(p.rate), hoursText: hoursToText(p.hours),
      })));
    }).catch((e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail || e.message)));
  }, [open, siteId]);

  const addRecipient = () => {
    const email = entry.trim().toLowerCase();
    if (!email) return;
    if (!/^[^@\s]+@[^@\s.]+\.[^@\s]+$/.test(email)) return toast.error("That does not look like an email address");
    if (recipients.includes(email)) return;
    setRecipients((r) => [...r, email]);
    setEntry("");
  };

  const save = async () => {
    setSaving(true);
    try {
      const numeric = (v) => (v === "" || v === null ? null : Number(v));
      await api.put(`/sites/${encodeURIComponent(siteId)}/settings`, {
        elec_rate: numeric(form.elec_rate),
        gas_rate: numeric(form.gas_rate),
        water_rate: numeric(form.water_rate),
        currency: form.currency || null,
        report_recipients: recipients,
        monthly_report_enabled: enabled,
        tou_enabled: touEnabled,
        demand_charge: numeric(demandCharge),
        demand_charge_basis: demandBasis,
        tou_periods: touPeriods
          .filter((p) => p.name && p.rate !== "" && p.hoursText)
          .map((p) => ({ name: p.name, rate: Number(p.rate), days: p.days, hours: textToHours(p.hoursText) })),
      });
      toast.success("Building settings saved");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="site-settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{siteDis} · building settings</DialogTitle>
        </DialogHeader>

        <p className="k-sublabel">
          Leave a rate blank to use the portfolio rate. These rates drive every cost figure for this building.
        </p>

        <div className="grid grid-cols-2 gap-4 mt-4">
          {[
            { key: "elec_rate", label: "Electricity rate (per kWh)", step: "0.001" },
            { key: "gas_rate", label: "Gas rate (per m³)", step: "0.001" },
            { key: "water_rate", label: "Water rate", step: "0.01" },
          ].map((f) => (
            <div key={f.key}>
              <Label className="k-sublabel">{f.label}</Label>
              <Input
                type="number"
                step={f.step}
                min="0"
                value={form[f.key]}
                placeholder="portfolio default"
                onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                data-testid={`site-rate-${f.key}`}
              />
            </div>
          ))}
          <div>
            <Label className="k-sublabel">Currency</Label>
            <Input
              value={form.currency}
              placeholder="CAD"
              maxLength={8}
              onChange={(e) => setForm((s) => ({ ...s, currency: e.target.value.toUpperCase() }))}
              data-testid="site-rate-currency"
            />
          </div>
        </div>

        <div className="mt-6 pt-5 border-t border-[#F0F2F1]">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-heading font-semibold text-[#212121]">Monthly report email</div>
              <p className="k-sublabel mt-0.5">Sent on the 1st with last month's figures.</p>
            </div>
            <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="site-report-toggle" />
          </div>

          <div className="mt-4 flex gap-2">
            <Input
              value={entry}
              onChange={(e) => setEntry(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addRecipient())}
              placeholder="owner@company.com"
              data-testid="site-recipient-input"
            />
            <button onClick={addRecipient} className="k-btn-outline shrink-0" data-testid="site-recipient-add">Add</button>
          </div>

          <div className="mt-3 flex flex-wrap gap-2" data-testid="site-recipient-list">
            {recipients.length === 0 ? (
              <span className="k-sublabel">No recipients yet</span>
            ) : (
              recipients.map((r) => (
                <span key={r} className="inline-flex items-center gap-1.5 text-xs bg-[#F3F8F7] border border-[#DCE8E4] text-[#156F5B] rounded-full pl-3 pr-2 py-1">
                  {r}
                  <button onClick={() => setRecipients((list) => list.filter((x) => x !== r))} data-testid={`site-recipient-remove-${r}`}>
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))
            )}
          </div>
        </div>

        <div className="mt-6 pt-5 border-t border-[#F0F2F1]">
          <div className="text-sm font-heading font-semibold text-[#212121]">Demand charge</div>
          <p className="k-sublabel mt-0.5">Billed on the highest kW in the period, like the utility does.</p>
          <div className="grid grid-cols-2 gap-4 mt-3">
            <div>
              <Label className="k-sublabel">Charge per kW</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={demandCharge}
                placeholder="none"
                onChange={(e) => setDemandCharge(e.target.value)}
                data-testid="demand-charge-input"
              />
            </div>
            <div>
              <Label className="k-sublabel">Peak measured over</Label>
              <select
                className="k-input text-sm"
                value={demandBasis}
                onChange={(e) => setDemandBasis(e.target.value)}
                data-testid="demand-basis-select"
              >
                <option value="monthly_peak">Any hour in the period</option>
                <option value="onpeak_peak">On-peak hours only</option>
              </select>
            </div>
          </div>
        </div>

        <TouEditor
          enabled={touEnabled}
          onEnabledChange={setTouEnabled}
          periods={touPeriods}
          onChange={setTouPeriods}
          template={template}
        />

        <div className="mt-6 flex justify-end gap-2">
          <button onClick={() => onOpenChange(false)} className="k-btn-outline">Cancel</button>
          <button onClick={save} disabled={saving} className="k-btn-primary" data-testid="site-settings-save">
            {saving ? "Saving…" : "Save settings"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
